/*
 *   File : detail.jsx
 *   Author : https://evoqins.com
 *   Description : mfd management detail component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { Fragment, useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { APIService } from "../../Service";
import { EmptyScreen, Loader } from "../../Components/Other";
import Icon from "../../Components/icons";
import { PrimaryButton } from "../../Components/Buttons";
import {
  ConfirmationModal,
  RejectMfdModal,
  SuccessModal,
  ViewModal,
} from "../../Components/Modals";

const STATUS_TEXT_CLASS = {
  Pending: "text-mfd-status-pending",
  Resubmitted: "text-mfd-status-resubmitted",
  Rejected: "text-copper-red",
  Approved: "text-medium-sea-green",
};

function Field({ label, children }) {
  return (
    <div className="d-flex flex-column justify-content-between e-primary-font-rubik">
      <span className="d-block fs-14 fw-400 lh-16 text-davys-gray mb-0">
        {label}
      </span>
      <div className="mt-2 fs-18 fw-400 lh-24 text-eerie-black">{children}</div>
    </div>
  );
}

export default function MfdManagementDetail() {
  const navigate = useNavigate();
  const fetchedIdRef = useRef(null);
  const { id } = useParams();
  const dispatch = useDispatch();
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailData, setDetailData] = useState({});

  const [confirmModal, setConfirmModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectSuccessModal, setRejectSuccessModal] = useState(false);

  const [approveLoading, setApproveLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState({
    url: "",
    title: "",
  });

  useEffect(() => {
    if (!id || fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;
    _getMfdDetail();
  }, [id]);

  useEffect(() => {
    dispatch(setNavLink(["/mfd-management"]));
    dispatch(setSearchEnabled(false));
  }, [dispatch]);

  // API - Get MFD Detail
  const _getMfdDetail = () => {
    setLoading(true);
    let is_post = true;
    let url = "partner/detail";
    let data = {
      id: Number(id),
    };

    APIService(is_post, url, data)
      .then((response) => {
        if (response.status_code === 200) {
          setDetailData(response.data);

          dispatch(
            setPageTitle([
              "MFD Management",
              response.data?.name ?? "MFD Management Detail",
            ]),
          );
        } else {
          setEmpty(true);
          setDetailData({});
          toast.dismiss();
          toast.error(
            response.message || "Failed to fetch mfd management details",
            {
              type: "error",
              className: "e-toast",
            },
          );

          navigate("/mfd-management");
        }
      })
      .catch((error) => {
        console.error("Error fetching mfd management details:", error);
        setEmpty(true);
        setDetailData({});
        toast.dismiss();
        toast.error("Failed to fetch mfd management details", {
          type: "error",
          className: "e-toast",
        });
        navigate("/mfd-management");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // API - Reject MFD Application
  const _approveMfd = () => {
    setApproveLoading(true);
    let is_post = true;
    let url = "partner/approve";
    let data = {
      id: Number(id),
    };

    APIService(is_post, url, data)
      .then((response) => {
        if (response.status_code === 200) {
          setSuccessModal(true);
          setConfirmModal(false);
        } else {
          toast.dismiss();
          toast.error(response.message || "Failed to approve", {
            type: "error",
            className: "e-toast",
          });
        }
      })
      .catch((error) => {
        toast.dismiss();
        toast.error("Failed to approve", {
          type: "error",
          className: "e-toast",
        });
      })
      .finally(() => {
        setApproveLoading(false);
      });
  };

  const handleViewDocument = (url, name) => {
    if (url && url !== "N/A") {
      setSelectedDocument({ url, title: name });
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDocument({ url: "", title: "" });
  };

  if (loading) {
    return (
      <div className="row">
        <div className="col-12">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  return empty ? (
    <EmptyScreen message={"No mfd management with current id"} />
  ) : (
    <Fragment>
      <div className="row">
        <div className="col-12">
          <div className="e-glass-card rounded-16 bg-transparent p-24">
            <div className="d-flex w-100 flex-column e-glass-card rounded-16 p-24">
              <div className="d-flex align-items-center text-eerie-black flex-grow-1">
                <div className="d-flex align-items-center gap-2 justify-content-between flex-wrap w-100">
                  <div className="d-flex align-items-center gap-">
                    <div className="d-flex e-liner-admin-image p-1x rounded-circle align-items-center justify-content-center">
                      {detailData?.profile_image ? (
                        <img
                          src={detailData.profile_image}
                          alt={detailData?.name || ""}
                          width={58}
                          height={58}
                          className="rounded-circle object-fit-cover border-facebook-blue"
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle border-facebook-blue bg-secondary text-white fw-semibold"
                          style={{
                            width: 58,
                            height: 58,
                            fontSize: 18,
                          }}
                        >
                          {(detailData?.name || "NA")
                            .trim()
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                      )}
                    </div>
                    <h5 className="fs-18 ps-19 lh-20 fw-500 text-capitalize mb-0">
                      {detailData?.name}
                    </h5>
                  </div>
                </div>
                {(detailData.status === "Pending" ||
                  detailData.status === "Resubmitted") && (
                    <div className="d-flex align-items-center justify-content-end gap-3 flex-wrap w-100">
                      <PrimaryButton
                        ClassName={`border-copper-red bg-transparent text-copper-red px-4 py-2 fs-16 fw-400 rounded-16`}
                        name={"Reject"}
                        onPress={() => setRejectModal(true)}
                      />
                      <PrimaryButton
                        ClassName={`border-oxford-blue bg-oxford-blue text-white  px-3 py-2 fs-16 fw-400 rounded-16`}
                        name={"Approve"}
                        onPress={() => {
                          setConfirmModal(true);
                        }}
                      />
                    </div>
                  )}
              </div>

              <div className="d-flex flex-wrap pt-4 align-items-start col-g-90 rg-24">
                <Field label="Submitted on">
                  <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                    {detailData?.submitted_on || "-"}
                  </p>
                </Field>
                <Field label="Email">
                  <p className="fw-400 fs-18 lh-24 text-eerie-black m-0 d-inline-flex align-items-center column-gap-1 flex-wrap">
                    <a
                      href={`mailto:${detailData?.email ?? ""}`}
                      className="text-black text-decoration-none"
                    >
                      {detailData?.email || "-"}
                    </a>
                    {detailData?.email ? (
                      <Icon icon="verified" size={16} />
                    ) : null}
                  </p>
                </Field>
                <Field label="Mobile number">
                  <p className="fw-400 fs-18 lh-24 text-eerie-black m-0 d-inline-flex align-items-center column-gap-1 flex-wrap">
                    <a
                      href={`tel:${detailData?.mobile ?? ""}`}
                      className="text-black text-decoration-none"
                    >
                      {detailData?.mobile || "-"}
                    </a>
                    {detailData?.mobile ? (
                      <Icon icon="verified" size={16} />
                    ) : null}
                  </p>
                </Field>
                <Field label="Status">
                  <p
                    className={`fw-400 fs-18 lh-24 m-0 text-capitalize ${STATUS_TEXT_CLASS[detailData?.status] || "text-eerie-black"}`}
                  >
                    {detailData?.status || "-"}
                  </p>
                </Field>

                {detailData.status === "Rejected" && (
                  <Field label="Rejected on">
                    <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                      {detailData?.rejected_on || "-"}
                    </p>
                  </Field>
                )}             
                {detailData.status === "Approved" && (
                  <Field label="Approved on">
                    <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                      {detailData?.accepted_on || "-"}
                    </p>
                  </Field>
                )}
              </div>
            </div>

            {detailData.rejected_reason !== null && (
              <div className="e-glass-card rounded-16 p-3  mt-3">
                <div className="d-flex align-items-center gap-1">
                  <p className="fs-16 fw-500 lh-20 mb-0 text-jet-gray">
                    Flagged Issues
                  </p>
                  <Icon icon="flagged" size={22} />
                </div>

                <ul className="pt-14 ps-4 m-0 text-black">
                  {detailData.rejected_reason.map((reason, index) => (
                    <li key={index} className="fw-400 fs-14 lh-24">{reason}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="d-flex w-100 flex-column e-glass-card rounded-16 p-4 gap-4 mt-3">
              <div className="e-border-bottom-platinum-gray pb-4">
                <div className="e-glass-card e-mfd-detail-card-shadow rounded-16 ">
                  <div className="e-mfd-tint-pan rounded-16 p-3 ">
                    <p className="fs-16 fw-500 lh-20 mb-0 text-jet-gray">
                      PAN Details
                    </p>

                    <div className="d-flex flex-wrap pt-3 align-items-start col-g-90 rg-24">
                      <Field label="PAN">
                        <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                          {detailData?.pan || "-"}
                        </p>
                      </Field>
                      <Field label="DOB">
                        <p className="fw-400 fs-18 lh-24 text-eerie-black m-0 d-inline-flex align-items-center column-gap-1 flex-wrap">
                          {detailData?.dob || "-"}
                        </p>
                      </Field>
                      <Field label="Nationality">
                        <p className="fw-400 fs-18 lh-24 text-eerie-black m-0 d-inline-flex align-items-center column-gap-1 flex-wrap">
                          {detailData?.nationality || "-"}
                        </p>
                      </Field>
                    </div>
                  </div>
                </div>
              </div>

              <div className="e-border-bottom-platinum-gray pb-4">
                <div className="e-glass-card e-mfd-detail-card-shadow rounded-16 ">
                  <div className="e-mfd-tint-arn rounded-16 p-3 ">
                    <p className="fs-16 fw-500 lh-20 mb-0 text-jet-gray">
                      ARN Details
                    </p>

                    <div className="d-flex flex-wrap pt-3 align-items-start col-g-90 rg-24">
                      <Field label="ARN number">
                        <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                          {detailData?.arn || "—"}
                        </p>
                      </Field>
                    </div>
                  </div>
                </div>

                <div className="e-glass-card e-mfd-detail-card-shadow rounded-16 mt-4 ">
                  <div className="e-mfd-tint-bank rounded-16 p-3 ">
                    <p className="fs-16 fw-500 lh-20 mb-0 text-jet-gray">
                      Bank Details
                    </p>

                    {detailData.bank_name !== null ? (
                      <div className="d-flex flex-wrap pt-3 align-items-start col-g-90 rg-24">
                        <Field label="Account holder name">
                          <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                            {detailData?.account_holder_name || "-"}
                          </p>
                        </Field>
                        <Field label="Bank name">
                          <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                            {detailData?.bank_name || "-"}
                          </p>
                        </Field>
                        <Field label="Account number">
                          <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                            {detailData?.account_number || "-"}
                          </p>
                        </Field>
                        <Field label="IFSC code">
                          <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                            {detailData?.ifsc || "-"}
                          </p>
                        </Field>
                      </div>
                    ) : (
                      <p className="mb-0 fs-14 text-black text-center p-4 fw-400 lh-24">
                        Bank Details Not Provided
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="e-glass-card e-mfd-detail-card-shadow rounded-16 ">
                <div className="e-mfd-tint-docs rounded-16 p-3">
                  <p className="fs-16 fw-500 lh-20 mb-0 text-jet-gray">
                    Documents
                  </p>

                  {detailData.pan_image !== null ||
                  detailData.arn_image !== null ? (
                    <div className="pt-3 row row-gap-3">
                      {detailData.pan_image !== null && (
                        <div className="col-lg-4 col-md-6 col-12">
                          <div className="py-3 px-20 e-mfd-document-wrapper">
                            <div className="d-flex align-items-center justify-content-between gap-1">
                              <div className="d-flex align-items-center col-g-12">
                                <Icon icon="document" size={24} />
                                <p className="fw-400 fs-12 lh-16 text-eerie-black m-0">
                                  Pan.png
                                </p>
                              </div>

                              <PrimaryButton
                                ClassName="bg-transparent lh-14 fs-12 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue "
                                name="View"
                                onPress={() =>
                                  handleViewDocument(
                                    detailData.pan_image,
                                    "PAN Card",
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {detailData.arn_image !== null && (
                        <div className="col-lg-4 col-md-6 col-12">
                          <div className="py-3 px-20 e-mfd-document-wrapper">
                            <div className="d-flex align-items-center justify-content-between gap-1">
                              <div className="d-flex align-items-center col-g-12">
                                <Icon icon="document" size={24} />
                                <p className="fw-400 fs-12 lh-16 text-eerie-black m-0">
                                  ARN registration.png
                                </p>
                              </div>

                              <PrimaryButton
                                ClassName="bg-transparent lh-14 fs-12 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue "
                                name="View"
                                onPress={() =>
                                  handleViewDocument(
                                    detailData.arn_image,
                                    "ARN Registration",
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="mb-0 fs-14 text-black text-center p-4 fw-400 lh-24">
                      Documents Not Provided
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RejectMfdModal
        open={rejectModal}
        onCancel={() => setRejectModal(false)}
        onConfirm={() => {
          setRejectModal(false);
          setRejectSuccessModal(true);
        }}
      />
      <ConfirmationModal
        open={confirmModal}
        title="Approve MFD application"
        message="You are about to approve this distributor’s application.
Once approved the user will gain full MFD access, they can start offering mutual fund investment. This action will be recorded for audit purposes"
        confirmText="Approve"
        loading={approveLoading}
        onConfirm={() => {
          _approveMfd();
        }}
        onCancel={() => setConfirmModal(false)}
      />
      <SuccessModal
        open={successModal}
        title={"Approval success"}
        message={"MFD application approved successfully."}
        onConfirm={() => {
          setConfirmModal(false);
          setSuccessModal(false);
          _getMfdDetail();
        }}
      />
      <SuccessModal
        open={rejectSuccessModal}
        title={"Rejection"}
        message={"MFD application rejected. User has been notified."}
        onConfirm={() => {
          setRejectSuccessModal(false);
          _getMfdDetail();
        }}
      />
      <ViewModal
        open={modalOpen}
        imageUrl={selectedDocument.url}
        title="View Document"
        onClose={handleCloseModal}
      />
    </Fragment>
  );
}
